/**
 * Supabase API layer for user role operations
 */

import { supabase } from '@/integrations/supabase/client';
import { createSuccessResponse, createErrorResponse } from '../errors';
import type { ApiResponse } from '../types/common';
import type { UserRoleAssignment } from '../types/responses';
import type { UserRole } from '@/constants';

/**
 * Fetch user role by user ID
 */
export async function fetchUserRole(userId: string): Promise<ApiResponse<UserRole | null>> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      return createErrorResponse(error);
    }

    return createSuccessResponse(data?.role as UserRole || null);
  } catch (error) {
    return createErrorResponse(error);
  }
}

/**
 * Check if user has a specific role
 */
export async function hasRole(
  userId: string,
  role: UserRole
): Promise<ApiResponse<boolean>> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', userId)
      .eq('role', role)
      .maybeSingle();

    if (error && error.code !== 'PGRST116') {
      return createErrorResponse(error);
    }

    return createSuccessResponse(!!data);
  } catch (error) {
    return createErrorResponse(error);
  }
}

/**
 * Assign a role to a user (admin only)
 */
export async function assignUserRole(
  userId: string,
  role: UserRole
): Promise<ApiResponse<UserRoleAssignment>> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .upsert({
        user_id: userId,
        role,
      })
      .select()
      .single();

    if (error) {
      return createErrorResponse(error);
    }

    return createSuccessResponse(data as UserRoleAssignment);
  } catch (error) {
    return createErrorResponse(error);
  }
}

/**
 * Fetch all users with their roles (admin only)
 */
export async function fetchAllUserRoles(): Promise<ApiResponse<UserRoleAssignment[]>> {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('*')
      .order('created_at', { ascending: false });

    if (error) {
      return createErrorResponse(error);
    }

    return createSuccessResponse(data as UserRoleAssignment[]);
  } catch (error) {
    return createErrorResponse(error);
  }
}
